import { AppEvent } from '@mews-distributor/common';
import { getEnterpriseDefaultLanguageCode, getEnterprisePricing } from '../../modules/entities/enterprises/selectors';
import { PROMOTED_SERVICE_RESERVATION_COUNT_CHANGED } from '../../modules/entities/reservations/actionTypes';
import { SERVICE_DATA_LOADED } from '../../modules/entities/services/actionTypes';
import { localizeProperty } from '../../modules/globalization/selectors';
import { getPromotedServiceName } from '../../modules/promotedServices';
import { PROMOTED_SERVICE_ADDED, PROMOTED_SERVICE_REMOVED } from '../../modules/resourceOrder/actionTypes';
import {
    PromotedServiceAddedPayload,
    PromotedServiceRemovedPayload,
    PromotedServiceReservationCountChangePayload,
    WithSimpleOccupancyFetchedPayload,
} from '../../modules/resourceOrder/payloadTypes';
import { getSelectedHotelId } from '../../modules/resourceOrder/selectors';
import { getCurrentDateAvailableCategories } from '../../selectors/availability';
import { getSelectedEnterpriseName } from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { CALENDAR_LOADED, PRICING_WITH_OCCUPANCY_FETCHED } from '../gtmActions';
import { getAvailableRooms, getImpressions, getMultiPropertyData } from './selectors';
import { AvailabilityLoadedPayload, TypedEventsOptions } from './types';

export const createTypedEvents = ({ getState, triggerEvent, triggerLegacyEvent }: TypedEventsOptions) => ({
    [SERVICE_DATA_LOADED]: () => {
        const state = getState();
        const enterpriseId = getSelectedHotelId(state);

        const pricingMode = getEnterprisePricing(state, { enterpriseId }) ?? null;
        const categories = getCurrentDateAvailableCategories(state, { enterpriseId });
        const availableRooms = getAvailableRooms(state, { enterpriseId, categories });
        const impressions = getImpressions(state, { enterpriseId, categories });
        const multiPropertyData = getMultiPropertyData(state, { enterpriseId });

        const data: AvailabilityLoadedPayload = {
            availableRooms,
            pricingMode,
            ecommerce: {
                impressions,
            },
            ...multiPropertyData,
        };

        triggerLegacyEvent(AppEvent.AvailabilityLoaded, data);
    },
    [CALENDAR_LOADED]: () => {
        triggerEvent(AppEvent.CalendarLoaded, {
            enterpriseName: getSelectedEnterpriseName(getState(), {}),
            page_location: '/calendar',
            page_title: 'Calendar',
        });
    },
    [PRICING_WITH_OCCUPANCY_FETCHED]: ({
        payload: { adultCount, childCount },
    }: {
        payload: WithSimpleOccupancyFetchedPayload;
    }) => {
        triggerEvent(AppEvent.FetchedWithOccupancy, { adultCount, childCount });
    },
    [PROMOTED_SERVICE_ADDED]: ({
        payload: { enterpriseId, promotedServiceId, count },
    }: {
        payload: PromotedServiceAddedPayload;
    }) => {
        const state = getState();
        const defaultLanguageCode = getEnterpriseDefaultLanguageCode(state, { enterpriseId });
        const property = getPromotedServiceName(state, { promotedServiceId });
        const promotedServiceName = localizeProperty(state, { property, languageCode: defaultLanguageCode });

        triggerEvent(AppEvent.PromotedServiceAdded, {
            promotedServiceId,
            count,
            promotedServiceName,
        });
    },
    [PROMOTED_SERVICE_REMOVED]: ({
        payload: { enterpriseId, promotedServiceId },
    }: {
        payload: PromotedServiceRemovedPayload;
    }) => {
        const state = getState();
        const defaultLanguageCode = getEnterpriseDefaultLanguageCode(state, { enterpriseId });
        const property = getPromotedServiceName(state, { promotedServiceId });
        const promotedServiceName = localizeProperty(state, { property, languageCode: defaultLanguageCode });

        triggerEvent(AppEvent.PromotedServiceRemoved, {
            promotedServiceId,
            promotedServiceName,
        });
    },
    [PROMOTED_SERVICE_RESERVATION_COUNT_CHANGED]: ({
        payload: { orderId, promotedServiceId, countChange },
    }: {
        payload: PromotedServiceReservationCountChangePayload;
    }) => {
        triggerEvent(AppEvent.PromotedServiceCountChanged, {
            orderId,
            promotedServiceId,
            countChange,
            ecommerce: {
                [countChange > 0 ? 'add' : 'remove']: {
                    products: [
                        {
                            serviceId: promotedServiceId,
                            quantity: Math.abs(countChange),
                        },
                    ],
                },
            },
        });
    },
});
